"use client";

import AnimatedLogoMark from "@/components/brand/AnimatedLogoMark";
import { usePathname } from "next/navigation";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { DISTRICTS, getDistrictBySlug } from "@/lib/districts";

// Inline SVG for Instagram - the project's lucide-react build
// doesn't include the Instagram glyph, so we ship the minimum
// path ourselves. 24x24 viewBox, stroke-based to match the rest
// of the footer chrome.
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37a4 4 0 1 1-7.91 1.27 4 4 0 0 1 7.91-1.27Z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

// Footer district links - one-time ranked by listing_count in
// price_aggregates (sale mode, week 2026-W15). Hand-curated rather
// than dynamic because (a) the ranks barely shift week-to-week in
// our data, (b) a hardcoded list ships on every page without a
// backend call, (c) SEO crawlers see a stable set. Refresh in a
// quarterly review pass as listing volumes drift.
//
// Ağ Şəhər is pinned at the end regardless of its modest listing
// count because it's a marquee, premium-tier area worth surfacing.
const FOOTER_DISTRICT_SLUGS: readonly string[] = [
  "hezi-aslanov",          // ~4,800 listings
  "insaatcilar",           // ~3,200
  "nesimi",                // ~3,100
  "28-may",                // ~2,800
  "neriman-nerimanov",     // ~2,800
  "elmler-akademiyasi",    // ~2,800
  "memar-ecemi",           // ~2,400
  "sah-ismayil-xetai",     // ~2,400
  "ehmedli",               // ~2,200
  "nerimanov",             // ~2,000
  "ag-seher",              // forced - White City, premium marquee area
];

const FOOTER_DISTRICTS = FOOTER_DISTRICT_SLUGS
  .map((slug) => getDistrictBySlug(slug))
  .filter((d): d is NonNullable<ReturnType<typeof getDistrictBySlug>> => !!d);

// Dev-only safety: surface any slug that drifted out of DISTRICTS
// (e.g. after a rename) before a broken link ships to prod.
if (
  process.env.NODE_ENV !== "production" &&
  FOOTER_DISTRICTS.length !== FOOTER_DISTRICT_SLUGS.length
) {
  const known = new Set(DISTRICTS.map((d) => d.slug));
  const missing = FOOTER_DISTRICT_SLUGS.filter((s) => !known.has(s));
  // eslint-disable-next-line no-console
  console.warn(`Footer: unresolved district slug(s): ${missing.join(", ")}`);
}

// Routes that opt out of the global footer. Full-bleed surfaces like
// /map use the entire viewport - rendering the footer below would
// push the map into scrollable territory, but the page is fixed-
// height so the footer would just sit invisibly off-screen and add
// dead vertical space anyway.
const FOOTER_HIDDEN_ROUTES = /^\/(az|ru|en)\/map(\/.*)?$/;

// The SEO search pages people look for most (2026-09-24, owner: link
// what was built from the footer, not the header). Plain links in every
// page's footer, so each of these carries sitewide internal weight.
const FOOTER_POPULAR: { href: string; key: string }[] = [
  { href: "/menziller/satis", key: "sale" },
  { href: "/menziller/kiraye", key: "rent" },
  { href: "/menziller/satis/yeni-tikili", key: "newbuild" },
  { href: "/menziller/satis/2-otaqli", key: "rooms2" },
  { href: "/menziller/satis/3-otaqli", key: "rooms3" },
  { href: "/menziller/satis/sahibinden", key: "owner" },
  { href: "/menziller/satis/agilli-ev", key: "smart" },
  { href: "/menziller/satis/deniz-menzereli", key: "sea" },
  { href: "/menziller/kiraye/esyali", key: "furnished" },
  { href: "/reytinq/yasayis-kompleksleri", key: "residences" },
  { href: "/sual/bakida-menzil-neceyedir", key: "price" },
  { href: "/sual/200000-manata-bakida-harada-menzil-almaq-olar", key: "budget" },
];

export default function Footer() {
  const t = useT();
  const year = new Date().getFullYear();
  const pathname = usePathname();
  if (pathname && FOOTER_HIDDEN_ROUTES.test(pathname)) return null;
  // Product/company/legal links FIRST, grouped into scannable columns
  // (owner call 2026-07-18: the district block buried the pages that
  // matter). Districts demote to a compact strip below; the brand +
  // copyright line closes the footer, where people expect it.
  const columns: {
    titleKey: Parameters<typeof t>[0];
    links: { href: string; labelKey: Parameters<typeof t>[0] }[];
  }[] = [
    {
      titleKey: "footer.col.tools",
      links: [
        { href: "/analyze", labelKey: "nav.analyze" },
        { href: "/calculate", labelKey: "nav.sell" },
        { href: "/listings", labelKey: "nav.listings" },
        { href: "/listings?category=commercial&property_kind=office", labelKey: "nav.offices" },
        { href: "/listings?category=commercial&property_kind=obyekt", labelKey: "nav.obyekts" },
        { href: "/listings?category=garage", labelKey: "nav.garages" },
        { href: "/compare", labelKey: "nav.compare" },
        { href: "/alerts", labelKey: "nav.alerts" },
      ],
    },
    {
      titleKey: "footer.col.discover",
      links: [
        { href: "/trends", labelKey: "footer.trends" },
        { href: "/menziller", labelKey: "footer.searches" },
        { href: "/reytinq/rayonlar", labelKey: "footer.rankings" },
        { href: "/residences", labelKey: "nav.residences" },
        { href: "/tikinti-sirketleri", labelKey: "nav.developers" },
        { href: "/tikinti-sirketleri/teklifler", labelKey: "nav.developer_offers" },
        { href: "/invest", labelKey: "nav.invest" },
        { href: "/auctions", labelKey: "sa.nav" },
        { href: "/trends/investor", labelKey: "nav.investor_compare" },
        { href: "/sual", labelKey: "nav.questions" },
        { href: "/blog", labelKey: "footer.blog" },
      ],
    },
    {
      titleKey: "footer.col.company",
      links: [
        { href: "/pricing", labelKey: "nav.pricing" },
        { href: "/about", labelKey: "footer.about" },
        { href: "/methodology", labelKey: "footer.methodology" },
        { href: "/contact", labelKey: "footer.contact" },
        { href: "/faq", labelKey: "nav.faq" },
        { href: "/whats-new", labelKey: "wn.nav" },
        { href: "/install", labelKey: "footer.install" },
      ],
    },
    {
      titleKey: "footer.col.legal",
      links: [
        { href: "/terms", labelKey: "legal.terms" },
        { href: "/privacy", labelKey: "legal.privacy" },
        { href: "/cookies", labelKey: "legal.cookies" },
        { href: "/disclaimer", labelKey: "legal.disclaimer" },
      ],
    },
  ];

  return (
    <footer className="border-t border-gray-100 bg-white mt-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-xs text-gray-500">
        {/* Link columns - 2-up on mobile, 4-up on sm+. py-1.5 keeps a
            thumbable tap target without the old py-2 sprawl. */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4">
          {columns.map((col) => (
            <nav key={col.titleKey} aria-label={t(col.titleKey)}>
              <div className="mb-2 text-[11px] font-semibold text-gray-600">
                {t(col.titleKey)}
              </div>
              <ul>
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link href={l.href} className="block py-1.5 hover:text-gray-900">
                      {t(l.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Districts - compact SEO strip, demoted below the product
            columns. Same curated top-10 set, inline-wrapped. */}
        <div className="mt-8 border-t border-gray-100 pt-5">
          <div className="mb-1 text-[11px] font-semibold text-gray-600">
            {t("footer.districts")}
          </div>
          <ul className="flex flex-wrap gap-x-4">
            {FOOTER_DISTRICTS.map((d) => (
              <li key={d.slug}>
                <Link href={`/trends/${d.slug}`} className="block py-1.5 hover:text-gray-900">
                  {d.name}
                </Link>
              </li>
            ))}
            <li>
              <Link href="/trends" className="block py-1.5 font-medium text-gray-600 hover:text-gray-900">
                {t("footer.all_districts")} →
              </Link>
            </li>
          </ul>
        </div>

        <div className="mt-5 border-t border-gray-100 pt-5">
          <div className="mb-1 text-[11px] font-semibold text-gray-600">
            {t("footer.popular")}
          </div>
          <ul className="flex flex-wrap gap-x-4">
            {FOOTER_POPULAR.map((l) => (
              <li key={l.href}>
                <Link href={l.href} className="block py-1.5 hover:text-gray-900">
                  {t(`footer.popular.${l.key}` as Parameters<typeof t>[0])}
                </Link>
              </li>
            ))}
            <li>
              <Link href="/menziller" className="block py-1.5 font-medium text-gray-600 hover:text-gray-900">
                {t("footer.searches")} →
              </Link>
            </li>
          </ul>
        </div>

        {/* Brand + copyright close the footer. */}
        <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-5 sm:flex-row">
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            {/* The same living mark as the header (owner, 2026-10-18). */}
            <AnimatedLogoMark size={20} markClassName="h-5 w-5">
              <span className="font-semibold text-gray-700" translate="no">{t("brand")}</span>
            </AnimatedLogoMark>
            <span>· © {year}</span>
          </div>
          {/* Instagram: external link to @menzil.ai. rel="me" is the
              signal Knowledge-Graph + IndieAuth tooling use to verify
              account ownership for the Organization card. */}
          <a
            href="https://www.instagram.com/menzil.ai/"
            target="_blank"
            rel="noopener noreferrer me"
            aria-label="Instagram"
            className="inline-flex items-center gap-1.5 hover:text-gray-900"
          >
            <InstagramIcon className="w-3.5 h-3.5" />
            Instagram
          </a>
        </div>
      </div>
    </footer>
  );
}
